import { FunctionComponent } from "react";
import { FieldErrors, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Input from "@mui/material/Input";

import { Slider, TextField } from "@mui/material";

import { SelectField } from "@/components/SelectField/SelectField";
import { InputField } from "@/components/InputField/InputField";

import { CompanyFilterSearch, CreateCompanyEnrich } from "../schema/companyEnrich";

interface WorkforceGrowthFiltersSectionProps {
	formValues: Partial<CreateCompanyEnrich>;
	setValue: UseFormSetValue<CreateCompanyEnrich>;
	externalError?: string | null;
	onErrorClear?: () => void;
}

export const WORKFORCE_PERIOD_OPTIONS = [
	{ title: "Any", value: null },
	{ title: "6 Months", value: "m6" },
	{ title: "1 Year", value: "y1" },
	{ title: "2 Years", value: "y2" },
];

export const WORKFORCE_DEPARTMENT_OPTIONS = [
	{ title: "Any", value: null },
	{ title: "Overall", value: "overall" },
	{ title: "C-Suite", value: "c_suite" },
	{ title: "Product Management", value: "product_management" },
	{ title: "Engineering & Technical", value: "engineering_technical" },
	{ title: "Design", value: "design" },
	{ title: "Education", value: "education" },
	{ title: "Finance", value: "finance" },
	{ title: "Human Resources", value: "human_resources" },
	{ title: "Information Technology", value: "information_technology" },
	{ title: "Legal", value: "legal" },
	{ title: "Marketing", value: "marketing" },
	{ title: "Medical & Health", value: "medical_health" },
	{ title: "Operations", value: "operations" },
	{ title: "Sales", value: "sales" },
	{ title: "Consulting", value: "consulting" },
];

type WorkforceGrowth = NonNullable<CompanyFilterSearch["workforceGrowth"]>;
type WorkforcePeriod = WorkforceGrowth["period"];
type WorkforceDepartment = WorkforceGrowth["department"];

export const WorkforceGrowthFiltersSection: FunctionComponent<
	WorkforceGrowthFiltersSectionProps
> = ({ formValues, setValue, externalError, onErrorClear }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.${key}`);

	const workforceGrowth = formValues.companyFilterSearch?.workforceGrowth;
	const minPercent = workforceGrowth?.minPercent ?? null;
	const maxPercent = workforceGrowth?.maxPercent ?? null;

	return (
		<>
			<div className="flex flex-col gap-2 w-full mb-4">
				<SelectField
					label={ts("workforcePeriodLabel")}
					name="workforcePeriod"
					selectedOption={workforceGrowth?.period ?? null}
					handleSelect={(val) => {
						setValue(
							"companyFilterSearch.workforceGrowth",
							{ ...(workforceGrowth ?? {}), period: val as WorkforcePeriod },
							{ shouldDirty: true, shouldValidate: true }
						);
					}}
					options={WORKFORCE_PERIOD_OPTIONS}
					hideEmptyValue
					showError={!!externalError}
				/>
			</div>
			<div className="flex flex-col gap-2 w-full mb-4">
				<SelectField
					label={ts("workforceDepartmentLabel")}
					name="workforceDepartment"
					selectedOption={workforceGrowth?.department ?? null}
					handleSelect={(val) => {
						setValue(
							"companyFilterSearch.workforceGrowth",
							{ ...(workforceGrowth ?? {}), department: val as WorkforceDepartment },
							{ shouldDirty: true, shouldValidate: true }
						);
					}}
					options={WORKFORCE_DEPARTMENT_OPTIONS}
					hideEmptyValue
					showError={!!externalError}
				/>
			</div>

			<div className="flex flex-row items-end gap-4 w-full mb-4">
				<div className="flex flex-col gap-2 w-full mb-4 ">
					<div className="flex justify-between">
						<label className="block text-sm font-medium text-gray-900 ">
							{ts("workforceGrowthLabel")}
						</label>
						<span
							className=" text-sm underline text-gray-700 cursor-pointer"
							onClick={() => {
								setValue(
									"companyFilterSearch.workforceGrowth",
									{ ...(workforceGrowth ?? {}), minPercent: null, maxPercent: null },
									{ shouldDirty: true, shouldValidate: true }
								);
							}}
						>
							Clear
						</span>
					</div>
					<div className="flex !items-center gap-5">
						{
							<TextField
								value={minPercent ?? ""}
								placeholder="Min"
								size="small"
								onChange={(e) => {
									const raw = e.target.value;
									const val =
										raw === ""
											? null
											: Math.min(Math.max(Number(raw), -100), (maxPercent ?? 100) - 1);

									setValue(
										"companyFilterSearch.workforceGrowth",
										{ ...(workforceGrowth ?? {}), minPercent: val },
										{ shouldDirty: true, shouldValidate: true }
									);
									onErrorClear?.();
								}}
								slotProps={{
									htmlInput: { step: 1, min: -100, max: 100, type: "number" },
									input: {
										endAdornment: (
											<span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>%</span>
										),
									},
								}}
								sx={{
									width: 80,
									"& .MuiOutlinedInput-root": {
										borderRadius: "8px",
										fontSize: 13,
										"& fieldset": { borderColor: "var(--color-border-tertiary)" },
										"&:hover fieldset": { borderColor: "var(--color-border-secondary)" },
										"&.Mui-focused fieldset": { borderColor: "#4d66f5", borderWidth: 1 },
									},
									"& input": { padding: "6px 8px", textAlign: "center" },
									"& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button": {
										display: "none",
									},
								}}
							/>
						}
						<Slider
							value={[minPercent ?? -100, maxPercent ?? 100]}
							onChange={(_, newValue) => {
								const [min, max] = newValue as [number, number];

								setValue(
									"companyFilterSearch.workforceGrowth",
									{ ...(workforceGrowth ?? {}), minPercent: min, maxPercent: max },
									{ shouldDirty: true, shouldValidate: true }
								);
								onErrorClear?.();
							}}
							min={-100}
							max={100}
							step={1}
							valueLabelDisplay="auto"
							valueLabelFormat={(v) => `${v}%`}
							disableSwap
							sx={{ flex: 1 }}
						/>
						<TextField
							value={maxPercent ?? ""}
							size="small"
							placeholder="Max"
							onChange={(e) => {
								const raw = e.target.value;
								const val =
									raw === ""
										? null
										: Math.max(Math.min(Number(raw), 100), (minPercent ?? -100) + 1);

								setValue(
									"companyFilterSearch.workforceGrowth",
									{ ...(workforceGrowth ?? {}), maxPercent: val },
									{ shouldDirty: true, shouldValidate: true }
								);
								onErrorClear?.();
							}}
							slotProps={{
								htmlInput: { step: 1, min: -100, max: 100, type: "number" },
								input: {
									endAdornment: (
										<span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>%</span>
									),
								},
							}}
							sx={{
								width: 80,
								"& .MuiOutlinedInput-root": {
									borderRadius: "8px",
									fontSize: 13,
									"& fieldset": { borderColor: "var(--color-border-tertiary)" },
									"&:hover fieldset": { borderColor: "var(--color-border-secondary)" },
									"&.Mui-focused fieldset": { borderColor: "#4d66f5", borderWidth: 1 },
								},
								"& input": { padding: "6px 8px", textAlign: "center" },
								"& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button": {
									display: "none",
								},
							}}
						/>
					</div>
				</div>
			</div>
			{externalError && <p className="text-xs text-status-error mb-2">{externalError}</p>}
		</>
	);
};
